import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useRecoilState, useRecoilValue } from 'recoil';
import DropdownSelector from '../../components/actions/DropdownSelector';
import SortOptionsSelector from '../../components/actions/SortOptionsSelector';
import * as Icon from '../../components/icons';
import InboxZero from '../../components/InboxZero';
import { InputSearch } from '../../components/inputs/InputSearch';
import LoadingIndicator from '../../components/LoadingIndicator';
import { ActionBar } from '../../components/nav/ActionBar';
import ActualityAggregatedStatsEventsList from '../components/ActualityAggregatedStatsEventsList';
import ActualityAggregatedStatsItem from '../components/ActualityAggregatedStatsItem';
import {
  EVENT_GROUPING_CONFIG,
  detectGroupingFromKeyFields,
} from '../constants/actuality.event.config';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import useGetActualityAggregationGroupColors from '../hooks/useGetActualityAggregationGroupColors';
import { actualityJobTimingMode } from '../store/actuality.jobs.state';
import { getAggregatedStatsKey } from '../store/actuality.kpi.helper';
import {
  actualityAggregatedStatsByGroupAndTime,
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsGroupBySecondary,
  actualityAggregatedStatsSelectedAggregatedMetric,
  actualityAggregatedStatsSelectedGroups,
  actualityAggregatedStatsSelectedSubGroup,
  actualityAggregatedStatsSubGroupsSortByDirection,
  actualityAggregatedStatsSubGroupsSortMode,
} from '../store/actuality.kpi.state';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregatedHqExtendedStatsDataColumn,
} from '../store/actuality.kpi.types';
import { actualityAggregatedStatsAllSelectedWhenEmpty } from '../store/actuality.kpi.ui.state';
import { actualityJobTimingModes } from '../store/feed.default';

type ActualityAggregatedStatsSubGroupPanelProps = {
  displayMode?: 'summary' | 'details' | 'process' | 'events';
  displayMetric?: ActualityAggregatedHqExtendedStatsDataColumn;
};

const ActualityAggregatedStatsSubGroupPanel: React.FC<
  ActualityAggregatedStatsSubGroupPanelProps
> = ({ displayMode }) => {
  const { t } = useTranslation('feed');
  const subGroupStats = useRecoilValue(actualityAggregatedStatsByGroupAndTime);

  const metric = useRecoilValue(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );
  const groupBy = useRecoilValue(actualityAggregatedStatsGroupBy);
  const [subGroupBy, setSubGroupBy] = useRecoilState(
    actualityAggregatedStatsGroupBySecondary,
  );
  const selectedGroups = useRecoilValue(actualityAggregatedStatsSelectedGroups);
  const [selectedSubGroup, setSelectedSubGroup] = useRecoilState(
    actualityAggregatedStatsSelectedSubGroup,
  );
  const [sortMode, setSortMode] = useRecoilState(
    actualityAggregatedStatsSubGroupsSortMode,
  );

  const [sortDirection, setSortDirection] = useRecoilState(
    actualityAggregatedStatsSubGroupsSortByDirection,
  );

  const [timingMode, setTimingMode] = useRecoilState(actualityJobTimingMode);
  const selectAllWhenNoExplicitSelection = useAtomValue(
    actualityAggregatedStatsAllSelectedWhenEmpty,
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [skipScrollToSelected, setSkipScrollToSelected] = useState(false);
  const getGroupColors = useGetActualityAggregationGroupColors();
  const metricDescriptors = useActualityAggregationMetricDescriptors();

  const virtuoso = useRef<VirtuosoHandle>(null);

  const panelId = 'ActualityAggregatedStatsSubGroupPanel';
  // Function to get stats fields for grouping using event config system
  const getStatsFieldsForGrouping = (
    stats: any,
    primaryGroupBy: string,
    secondaryGroupBy: string,
  ): ActualityAggregatedHqExtendedStatsDataColumn[] => {
    // Detect grouping type from the key fields
    const detectedGrouping = detectGroupingFromKeyFields([
      primaryGroupBy,
      secondaryGroupBy,
    ]);
    const groupingConfig = EVENT_GROUPING_CONFIG[detectedGrouping];

    // Get primary stats from the grouping config
    const configuredStats = groupingConfig.primaryStats;

    // Map event config field names to aggregated data field names where they exist
    const mappedFields = configuredStats
      .map(fieldKey => {
        // Direct mapping for fields that exist in both systems
        if (fieldKey in stats) {
          return fieldKey;
        }

        // Map event config field names to aggregated data field names
        switch (fieldKey) {
          case 'duration':
            return 'analyzedDuration';
          case 'distance':
            return 'analyzedDistance';
          case 'quantity':
            return 'uomCount';
          default:
            // Check if the field exists in the stats object
            return fieldKey in stats ? fieldKey : null;
        }
      })
      .filter((field): field is string => field !== null && field in stats)
      .slice(0, 4); // Limit to avoid too many fields

    // Fallback to some default fields if no mapped fields found
    if (mappedFields.length === 0) {
      return ['analyzedDuration', 'analyzedDistance'];
    }

    return mappedFields as ActualityAggregatedHqExtendedStatsDataColumn[];
  };

  const searchRegex = new RegExp(searchTerm, 'i');
  const filteredStats = useMemo(() => {
    const filtered = _.filter(subGroupStats, row => {
      const isVisible =
        (selectAllWhenNoExplicitSelection && _.isEmpty(selectedGroups)) ||
        _.some(selectedGroups, g =>
          _.isEqual(g, getAggregatedStatsKey(row, [groupBy])),
        );
      return (
        isVisible &&
        (_.isEmpty(searchTerm) ||
          searchRegex.test(_.get(row, subGroupBy)?.toString()))
      );
    });

    return filtered;
  }, [
    subGroupStats,
    selectedGroups,
    groupBy,
    subGroupBy,
    searchTerm,
    selectAllWhenNoExplicitSelection,
  ]);

  useEffect(() => {
    if (!skipScrollToSelected && !_.isNil(selectedSubGroup)) {
      const index = _.findIndex(filteredStats, row =>
        _.isEqual(
          selectedSubGroup,
          getAggregatedStatsKey(row, [groupBy, subGroupBy]),
        ),
      );
      if (index > -1) {
        virtuoso.current.scrollToIndex({
          index,
          align: 'center',
          behavior: 'auto',
        });
      }
    }
    setSkipScrollToSelected(false);
  }, [
    selectedSubGroup,
    skipScrollToSelected,
    filteredStats,
    groupBy,
    subGroupBy,
  ]);

  const hasFilteredStats = !_.isEmpty(filteredStats);

  const handleSelection = (
    selectedStats: Partial<ActualityAggregatedHqExtendedDataRow>[],
  ) => {
    setSkipScrollToSelected(true);
    setSelectedSubGroup(_.head(selectedStats));
  };

  return (
    <div
      data-component="ActualityAggregatedStatsGroupPanel"
      className={classNames('relative flex h-full flex-col overflow-hidden')}
    >
      <ActionBar
        transparent
        className={classNames(
          'p-2',
          'bg-sidebar-title/40',
          'saturate-110 backdrop-blur',
          'top-18 sticky',
          'z-header_screen',
          'gap-1',
        )}
      >
        <InputSearch
          placeholder={t('Search Activity')}
          value={searchTerm}
          onChange={(value: string) => setSearchTerm(value)}
          panelTitleMode
          classNamePlaceholder="!left-2 !top-2"
          classNameInput="!px-2 !py-1"
        />
        <DropdownSelector
          className="shrink-0 text-xs"
          value={timingMode}
          values={[...actualityJobTimingModes]}
          renderValue={v => `${t`Time View: `}${v}`}
          onChange={selectedMode => setTimingMode(selectedMode)}
          valuePrimary
          light
          w_lg
          DropAlignRight
          iconValue={Icon.TimeFull}
          iconValueOnly
          iconClass={classNames('w-4 h-4 xl:w-5 xl:h-5', 'bg-menu rounded p-1')}
        />

        {/* Unified Sort Options with 4 options */}
        <SortOptionsSelector
          // Panel identification
          panelId={panelId}
          sortMode={sortMode}
          sortDirection={sortDirection}
          onChange={value => {
            setSortMode(value.mode);
            setSortDirection(value.direction);
          }}
          groupSortLabel={t('Sort by Group')}
          // UI configuration - Use unified sort dropdown
          className="shrink-0 text-xs"
          showUnifiedSort={true}
          showSortDirection={false}
          showSortMetric={false}
          showGroupSort={false}
          showSortModeToggle={false}
          compact={false}
          panelMode={true}
          vertical={true}
          w_lg={true}
          DropAlignRight={true}
          // Labels
          sortDirectionLabel={t('Sort Direction')}
          sortMetricLabel={t('Sort Metric')}
        />
      </ActionBar>
      {!hasFilteredStats ? (
        <InboxZero
          selfCenter
          message={searchTerm ? t`No matching data found` : t`No data found`}
        />
      ) : (
        <>
          <div
            data-component="EventsList"
            className={classNames(
              'flex h-full flex-col overflow-hidden',
              'gap-0.5',
            )}
          >
            <Virtuoso
              className={classNames('h-full overflow-x-hidden pb-[12.5rem]')}
              ref={virtuoso}
              data={filteredStats}
              itemContent={(index, stats) => {
                const statsKey = getAggregatedStatsKey(stats, [
                  groupBy,
                  subGroupBy,
                ]);
                const key = _.values(statsKey).join('-');

                const isSelected = _.isEqual(selectedSubGroup, statsKey);

                return (
                  <ActualityAggregatedStatsItem
                    className={classNames(
                      'mb-1',
                      isSelected ? 'border-sidebar-title/40 border-2' : '',
                    )}
                    key={`stats-${key}}`}
                    data={stats}
                    keyFields={[groupBy, subGroupBy]}
                    colorFields={[groupBy]}
                    selected={_.compact([selectedSubGroup])}
                    onSelectionChanged={handleSelection}
                    mode="fieldsOnly"
                    defaultFieldsLimit={1}
                    summaryFields={getStatsFieldsForGrouping(
                      stats,
                      groupBy,
                      subGroupBy,
                    )}
                    displayMode={displayMode}
                  >
                    {isSelected && (
                      <Suspense
                        fallback={
                          <LoadingIndicator
                            transparent
                            message={`Loading Events...`}
                            oneLiner
                            className="px-4 py-4"
                          />
                        }
                      >
                        <ActualityAggregatedStatsEventsList />
                      </Suspense>
                    )}
                  </ActualityAggregatedStatsItem>
                );
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ActualityAggregatedStatsSubGroupPanel;
